import React, { useMemo, useCallback } from 'lib/react';
import PropTypes from 'prop-types';
import {
  EVButton,
  EVGrid,
  EVLink,
  EVPaper,
  EVSkeleton,
} from 'lib/components';
import { useDispatch, useSelector } from 'lib/redux';
import { useTranslation } from 'lib/translation';
import clsx from 'lib/clsx';
import useMediaQuery from 'hooks/useMediaQuery';
import { placeOrder as placeOrderAction, resetFinalizeOrderPage } from 'features/finalizeOrder/FinalizeOrder.actions';
import history from 'store/CreateHistory';
import { ORDER_ROUTE, ORDER_BLUEPRINT_ROUTE } from 'constants/RouteConstants';
import { LEGACY_PORTAL_LINKS, PRODUCT_IDS } from 'constants/AppConstants';
import Typography from 'commonComponents/typography';
import HelpTooltip from 'commonComponents/helpTooltip/HelpTooltip.component';
import { isEntitledForHidePricingSelector } from 'utils/appSelectors.utils';
import {
  ADDON_SUBSCRIPTION_PRODUCTS,
  DELIVERY_SUBSCRIPTION_PRODUCTS,
  PRIMARY_SUBSCRIPTION_PRODUCTS,
  REGULAR_DELIVERY_PRODUCT_ID,
  getSubscriptionEsxPrice,
} from 'features/order/Order.constants';
import { ESX_COMPATIBLE_PRODUCTS } from 'features/order/components/Product/ProductList.constants';
import { getCurrencySymbol } from 'features/order/Order.utils';
import OrderSummarySubscriptionStyles from './OrderSummarySubscriptionStyles';
import PlacingOrderLoader from '../PlacingOrderLoader.component';

function resolveDeliveryMeta(subscriptionProductInfo, selectedDeliveryId) {
  if (selectedDeliveryId === REGULAR_DELIVERY_PRODUCT_ID) return null;
  const fromAllowed = (subscriptionProductInfo?.allowedDeliveries ?? []).find(
    (d) => Number(d.productId) === selectedDeliveryId,
  );
  if (fromAllowed) return fromAllowed;
  return DELIVERY_SUBSCRIPTION_PRODUCTS.find((d) => Number(d.productId) === selectedDeliveryId) ?? null;
}

function resolveEstimatedTotalDisplay({
  isEntitledForHidePricing,
  isSubscriptionProduct,
  subscriptionEstimatedTotal,
  estimatedCostResponse,
}) {
  if (isEntitledForHidePricing) return null;
  if (isSubscriptionProduct && subscriptionEstimatedTotal) return subscriptionEstimatedTotal;
  if (!estimatedCostResponse?.total) return null;
  return estimatedCostResponse.total.cost;
}

function UnitBadge({ classes, units, t }) {
  return (
    <span className={classes.unitBadge}>
      {units}
      {' '}
      {units === 1 ? t('finalize_order__order_summary__unit') : t('finalize_order__order_summary__units')}
    </span>
  );
}

UnitBadge.propTypes = {
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  units: PropTypes.number.isRequired,
  t: PropTypes.func.isRequired,
};

function OveragePrice({
  classes,
  isEntitledForHidePricing,
  overagePriceDisplay,
  formatSubscriptionLinePrice,
  t,
}) {
  if (isEntitledForHidePricing) return <Typography>—</Typography>;
  if (!overagePriceDisplay) return null;
  return (
    <span className={classes.overagePriceRow}>
      <HelpTooltip title={t('finalize_order__order_summary__overage_price_tooltip')} />
      <Typography className={classes.overagePriceText}>
        {formatSubscriptionLinePrice(Number(overagePriceDisplay))}
      </Typography>
    </span>
  );
}

OveragePrice.propTypes = {
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  isEntitledForHidePricing: PropTypes.bool.isRequired,
  overagePriceDisplay: PropTypes.string,
  formatSubscriptionLinePrice: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
};

OveragePrice.defaultProps = {
  overagePriceDisplay: null,
};

function SubscriptionExtras({
  classes,
  showDelivery,
  showAddon,
  selectedDeliveryMeta,
  formatSubscriptionLinePrice,
  deliveryTotal,
  addonTotal,
}) {
  const { t } = useTranslation();
  if (!showDelivery && !showAddon) return null;
  return (
    <div className={classes.subscriptionExtras}>
      {showDelivery ? (
        <div className={classes.subscriptionExtraLine}>
          <div className={classes.subscriptionExtraLineText}>
            <span className={classes.subscriptionExtraLineHeading}>
              {t('finalize_order__order_summary__delivery_option')}
            </span>
            <span className={classes.subscriptionExtraLineValue}>
              {selectedDeliveryMeta?.name ?? ''}
            </span>
          </div>
          <span className={classes.subscriptionExtraLinePrice}>
            {formatSubscriptionLinePrice(deliveryTotal)}
          </span>
        </div>
      ) : null}
      {showAddon ? (
        <div className={classes.subscriptionExtraLine}>
          <div className={classes.subscriptionExtraLineText}>
            <span className={classes.subscriptionExtraLineHeading}>
              {t('finalize_order__order_summary__addon')}
            </span>
            <span className={classes.subscriptionExtraLineValue}>
              {ADDON_SUBSCRIPTION_PRODUCTS.find((a) => a.productId === PRODUCT_IDS.ESX)?.name ?? 'ESX'}
            </span>
          </div>
          <span className={classes.subscriptionExtraLinePrice}>
            {formatSubscriptionLinePrice(addonTotal)}
          </span>
        </div>
      ) : null}
    </div>
  );
}

SubscriptionExtras.propTypes = {
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  showDelivery: PropTypes.bool.isRequired,
  showAddon: PropTypes.bool.isRequired,
  selectedDeliveryMeta: PropTypes.shape({
    name: PropTypes.string,
  }),
  formatSubscriptionLinePrice: PropTypes.func.isRequired,
  deliveryTotal: PropTypes.number.isRequired,
  addonTotal: PropTypes.number.isRequired,
};

SubscriptionExtras.defaultProps = {
  selectedDeliveryMeta: null,
};

function ProductLine({
  classes,
  quantity,
  name,
  description,
  isOverageLine,
  unitsPerProduct,
  isEntitledForHidePricing,
  overagePriceDisplay,
  formatSubscriptionLinePrice,
  t,
}) {
  const overageSuffix = isOverageLine ? ` ${t('finalize_order__order_summary__overage_suffix')}` : '';
  const title = `${quantity}x ${name}${overageSuffix}`;
  return (
    <div className={classes.productBlock}>
      <div className={classes.productBlockHeader}>
        <Typography className={classes.productTitle}>{title}</Typography>
        {isOverageLine && overagePriceDisplay ? (
          <OveragePrice
            classes={classes}
            isEntitledForHidePricing={isEntitledForHidePricing}
            overagePriceDisplay={overagePriceDisplay}
            formatSubscriptionLinePrice={formatSubscriptionLinePrice}
            t={t}
          />
        ) : (
          <UnitBadge classes={classes} units={unitsPerProduct} t={t} />
        )}
      </div>
      {description ? (
        <Typography className={classes.productDescription}>{description}</Typography>
      ) : null}
    </div>
  );
}

ProductLine.propTypes = {
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  quantity: PropTypes.number.isRequired,
  name: PropTypes.string.isRequired,
  description: PropTypes.string,
  isOverageLine: PropTypes.bool.isRequired,
  unitsPerProduct: PropTypes.number.isRequired,
  isEntitledForHidePricing: PropTypes.bool.isRequired,
  overagePriceDisplay: PropTypes.string,
  formatSubscriptionLinePrice: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
};

ProductLine.defaultProps = {
  description: '',
  overagePriceDisplay: null,
};

function OrderSummarySubscriptionProductSection({
  classes,
  productDisplay,
  subscriptionLineItems,
  isEntitledForHidePricing,
  overagePriceDisplay,
  showSubscriptionDeliveryLine,
  showSubscriptionAddonLine,
  selectedDeliveryMeta,
  formatSubscriptionLinePrice,
  subscriptionDeliveryLineTotal,
  subscriptionAddonLineTotal,
  t,
}) {
  if (!productDisplay) return null;

  // Non-subscription / legacy single row (no Nx split).
  if (!subscriptionLineItems?.length) {
    return (
      <div className={classes.productBlock} key={productDisplay.id}>
        <div className={classes.productBlockHeader}>
          <Typography className={classes.productTitle}>{productDisplay.name}</Typography>
        </div>
        {productDisplay.description ? (
          <Typography className={classes.productDescription}>{productDisplay.description}</Typography>
        ) : null}
        <SubscriptionExtras
          classes={classes}
          showDelivery={showSubscriptionDeliveryLine}
          showAddon={showSubscriptionAddonLine}
          selectedDeliveryMeta={selectedDeliveryMeta}
          formatSubscriptionLinePrice={formatSubscriptionLinePrice}
          deliveryTotal={subscriptionDeliveryLineTotal}
          addonTotal={subscriptionAddonLineTotal}
        />
      </div>
    );
  }

  return (
    <div className={classes.subscriptionProductLines} key={productDisplay.id}>
      {subscriptionLineItems.map((line) => (
        <ProductLine
          key={line.key}
          classes={classes}
          quantity={line.quantity}
          name={productDisplay.name}
          description={productDisplay.description}
          isOverageLine={line.isOverageLine}
          unitsPerProduct={line.unitsPerProduct}
          isEntitledForHidePricing={isEntitledForHidePricing}
          overagePriceDisplay={overagePriceDisplay}
          formatSubscriptionLinePrice={formatSubscriptionLinePrice}
          t={t}
        />
      ))}
      <SubscriptionExtras
        classes={classes}
        showDelivery={showSubscriptionDeliveryLine}
        showAddon={showSubscriptionAddonLine}
        selectedDeliveryMeta={selectedDeliveryMeta}
        formatSubscriptionLinePrice={formatSubscriptionLinePrice}
        deliveryTotal={subscriptionDeliveryLineTotal}
        addonTotal={subscriptionAddonLineTotal}
      />
    </div>
  );
}

OrderSummarySubscriptionProductSection.propTypes = {
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  productDisplay: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    name: PropTypes.string,
    description: PropTypes.string,
  }),
  subscriptionLineItems: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string,
    quantity: PropTypes.number,
    isOverageLine: PropTypes.bool,
    unitsPerProduct: PropTypes.number,
  })),
  isEntitledForHidePricing: PropTypes.bool.isRequired,
  overagePriceDisplay: PropTypes.string,
  showSubscriptionDeliveryLine: PropTypes.bool.isRequired,
  showSubscriptionAddonLine: PropTypes.bool.isRequired,
  selectedDeliveryMeta: PropTypes.shape({ name: PropTypes.string }),
  formatSubscriptionLinePrice: PropTypes.func.isRequired,
  subscriptionDeliveryLineTotal: PropTypes.number.isRequired,
  subscriptionAddonLineTotal: PropTypes.number.isRequired,
  t: PropTypes.func.isRequired,
};

OrderSummarySubscriptionProductSection.defaultProps = {
  productDisplay: null,
  subscriptionLineItems: null,
  overagePriceDisplay: null,
  selectedDeliveryMeta: null,
};

function useOrderSummarySubscriptionState({ paymentSectionRef }) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const isMobile = useMediaQuery(640);

  const {
    placeOrderLoading,
    estimatedCostResponse,
    fetchingEstimatedCost,
    bluePrintFileId,
    upgradeFromReportId,
    productSets,
    selectedPrimaryProduct,
    primarySubscriptionProductsWithUsage,
    subscriptionEstimatedTotal,
    selectedPrimaryDeliveryProduct,
    selectedOtherProducts,
    currencyTypeId,
  } = useSelector(({
    finalizeOrder,
    placeOrder,
    order,
    userData,
  }) => ({
    placeOrderLoading: finalizeOrder.placeOrderLoading,
    estimatedCostResponse: finalizeOrder.estimatedCostResponse,
    fetchingEstimatedCost: finalizeOrder.fetchingEstimatedCost,
    bluePrintFileId: placeOrder.bluePrintFileId,
    upgradeFromReportId: finalizeOrder.upgradeFromReportId,
    productSets: placeOrder.productSets ?? [],
    selectedPrimaryProduct: order.selectedPrimaryProduct,
    primarySubscriptionProductsWithUsage: order.primarySubscriptionProductsWithUsage ?? [],
    subscriptionEstimatedTotal: order.subscriptionEstimatedTotal,
    selectedPrimaryDeliveryProduct: order.selectedPrimaryDeliveryProduct,
    selectedOtherProducts: order.selectedOtherProducts ?? {},
    currencyTypeId: userData?.initParameters?.currencyTypeId,
  }));

  const isUpgradeFlow = Boolean(upgradeFromReportId);
  const upgradeProductSet = isUpgradeFlow ? productSets[0] : null;
  const upgradePrimaryProductId = upgradeProductSet?.primaryProductId ?? null;
  const upgradeAddOnProductIds = upgradeProductSet?.addOnProductIds ?? [];

  const mapPinsCount = useSelector(({ orderAddresses, placeOrder, finalizeOrder: fo }) => {
    if (fo.upgradeFromReportId) return 1;
    return placeOrder.bluePrintFileId ? 1 : (Number(orderAddresses?.mapPoints?.length) || 0);
  });
  const isEntitledForHidePricing = useSelector(isEntitledForHidePricingSelector);

  const subscriptionProductInfo = useMemo(() => {
    const primaryProductId = isUpgradeFlow
      ? upgradePrimaryProductId
      : selectedPrimaryProduct?.ProductID;
    if (!primaryProductId) return null;
    return primarySubscriptionProductsWithUsage.find(
      (p) => Number(p.productId) === Number(primaryProductId),
    );
  }, [
    isUpgradeFlow,
    upgradePrimaryProductId,
    selectedPrimaryProduct?.ProductID,
    primarySubscriptionProductsWithUsage,
  ]);

  const isSubscriptionProduct = Boolean(subscriptionProductInfo?.isSubscription);
  const pinCount = Math.max(Number(mapPinsCount) || 0, 1);
  // Upgrade always consumes 1 unit per report (Full House is 2 only on new-order flow).
  const unitsPerProduct = isUpgradeFlow
    ? 1
    : Math.max(Number(subscriptionProductInfo?.unitBadgeCount ?? 1) || 1, 1);
  const unitsOrdered = pinCount * unitsPerProduct;
  const totalUnits = Number(subscriptionProductInfo?.includedQty) || 0;
  const usedBeforeOrder = Number(subscriptionProductInfo?.usedQty) || 0;
  const remainingIncludedUnits = Math.max(totalUnits - usedBeforeOrder, 0);

  const includedUnitsInOrder = isSubscriptionProduct
    ? Math.min(unitsOrdered, remainingIncludedUnits)
    : 0;
  const overageUnitsInOrder = isSubscriptionProduct
    ? Math.max(unitsOrdered - remainingIncludedUnits, 0)
    : 0;

  // Whole-product split for Nx labels (pins fully covered first; remainder are overage).
  const includedProductQty = isSubscriptionProduct
    ? Math.min(pinCount, Math.floor(remainingIncludedUnits / unitsPerProduct))
    : 0;
  const overageProductQty = isSubscriptionProduct
    ? Math.max(pinCount - includedProductQty, 0)
    : 0;

  const isOverage = overageUnitsInOrder > 0;

  const overagePriceDisplay = useMemo(() => {
    const rate = Number(subscriptionProductInfo?.overageRatePerUnit);
    if (!Number.isFinite(rate) || rate <= 0) return null;
    return Number.isInteger(rate) ? String(rate) : rate.toFixed(2);
  }, [subscriptionProductInfo?.overageRatePerUnit]);

  const selectedDeliveryId = Number(
    isUpgradeFlow
      ? (upgradeProductSet?.deliveryProductId ?? REGULAR_DELIVERY_PRODUCT_ID)
      : (selectedPrimaryDeliveryProduct ?? REGULAR_DELIVERY_PRODUCT_ID),
  );

  const selectedDeliveryMeta = useMemo(
    () => resolveDeliveryMeta(subscriptionProductInfo, selectedDeliveryId),
    [subscriptionProductInfo, selectedDeliveryId],
  );

  const hasSubscriptionProductMetadata = Boolean(subscriptionProductInfo);

  const showSubscriptionDeliveryLine = useMemo(() => (
    hasSubscriptionProductMetadata
    && selectedDeliveryId !== REGULAR_DELIVERY_PRODUCT_ID
    && Boolean(selectedDeliveryMeta)
  ), [hasSubscriptionProductMetadata, selectedDeliveryId, selectedDeliveryMeta]);

  const showSubscriptionAddonLine = useMemo(() => {
    if (!hasSubscriptionProductMetadata) return false;
    const primaryProductId = isUpgradeFlow
      ? upgradePrimaryProductId
      : selectedPrimaryProduct?.ProductID;
    const hasEsxSelected = isUpgradeFlow
      ? upgradeAddOnProductIds.includes(PRODUCT_IDS.ESX)
      : Object.hasOwn(selectedOtherProducts, PRODUCT_IDS.ESX);
    if (!hasEsxSelected) return false;
    return ESX_COMPATIBLE_PRODUCTS.includes(Number(primaryProductId));
  }, [
    hasSubscriptionProductMetadata,
    isUpgradeFlow,
    upgradeAddOnProductIds,
    selectedOtherProducts,
    upgradePrimaryProductId,
    selectedPrimaryProduct?.ProductID,
  ]);

  const subscriptionDeliveryLineTotal = useMemo(() => {
    if (!showSubscriptionDeliveryLine || !selectedDeliveryMeta) return 0;
    const perPin = Number(selectedDeliveryMeta.price) || 0;
    return Number((perPin * pinCount).toFixed(2));
  }, [showSubscriptionDeliveryLine, selectedDeliveryMeta, pinCount]);

  const subscriptionAddonLineTotal = useMemo(() => {
    if (!showSubscriptionAddonLine) return 0;
    const perPin = getSubscriptionEsxPrice(subscriptionProductInfo);
    return Number((perPin * pinCount).toFixed(2));
  }, [showSubscriptionAddonLine, subscriptionProductInfo, pinCount]);

  const formatSubscriptionLinePrice = useCallback((amount) => {
    if (isEntitledForHidePricing) return '—';
    const sym = getCurrencySymbol(currencyTypeId);
    const n = Number(amount) || 0;
    const str = Number.isInteger(n) ? String(n) : n.toFixed(2);
    return `${sym}${str}`;
  }, [isEntitledForHidePricing, currencyTypeId]);

  const productDisplay = useMemo(() => {
    const productId = isUpgradeFlow ? upgradePrimaryProductId : selectedPrimaryProduct?.ProductID;
    if (!productId) return null;
    const fromConstants = PRIMARY_SUBSCRIPTION_PRODUCTS.find((p) => Number(p.productId) === Number(productId));
    const name = isUpgradeFlow
      ? (subscriptionProductInfo?.name ?? fromConstants?.name ?? '')
      : (selectedPrimaryProduct.Name ?? selectedPrimaryProduct.Description ?? fromConstants?.name ?? '');
    const description = fromConstants?.description ?? selectedPrimaryProduct?.Description ?? '';
    return {
      id: productId,
      name,
      description,
    };
  }, [
    isUpgradeFlow,
    upgradePrimaryProductId,
    selectedPrimaryProduct,
    subscriptionProductInfo?.name,
  ]);

  const subscriptionLineItems = useMemo(() => {
    if (!isSubscriptionProduct) return null;
    const lines = [];
    if (includedProductQty > 0) {
      lines.push({
        key: 'included',
        quantity: includedProductQty,
        isOverageLine: false,
        unitsPerProduct,
      });
    }
    if (overageProductQty > 0) {
      lines.push({
        key: 'overage',
        quantity: overageProductQty,
        isOverageLine: true,
        unitsPerProduct,
      });
    }
    // Edge: subscription product but 0 pins somehow — still show one included-style row.
    if (!lines.length && productDisplay) {
      lines.push({
        key: 'included',
        quantity: 1,
        isOverageLine: false,
        unitsPerProduct,
      });
    }
    return lines;
  }, [
    isSubscriptionProduct,
    includedProductQty,
    overageProductQty,
    unitsPerProduct,
    productDisplay,
  ]);

  const estimatedTotalDisplay = useMemo(
    () => resolveEstimatedTotalDisplay({
      isEntitledForHidePricing,
      isSubscriptionProduct,
      subscriptionEstimatedTotal,
      estimatedCostResponse,
    }),
    [estimatedCostResponse, isEntitledForHidePricing, isSubscriptionProduct, subscriptionEstimatedTotal],
  );

  const estimatedTotalLabel = estimatedCostResponse?.total?.name
    ? estimatedCostResponse.total.name
    : `${t('EstimatedTotal')} (USD)`;

  const handleSubmit = useCallback(async () => {
    const transientTokenData = await paymentSectionRef.current.tokenize();
    dispatch(placeOrderAction(transientTokenData));
  }, [dispatch, paymentSectionRef]);

  const handleEditOrder = useCallback(() => {
    dispatch(resetFinalizeOrderPage(true));
    history.unblock();
    history.push(bluePrintFileId ? ORDER_BLUEPRINT_ROUTE : ORDER_ROUTE);
  }, [dispatch, bluePrintFileId]);

  return {
    isMobile,
    placeOrderLoading,
    fetchingEstimatedCost,
    productDisplay,
    subscriptionLineItems,
    isOverage,
    isEntitledForHidePricing,
    overagePriceDisplay,
    showSubscriptionDeliveryLine,
    showSubscriptionAddonLine,
    selectedDeliveryMeta,
    formatSubscriptionLinePrice,
    subscriptionDeliveryLineTotal,
    subscriptionAddonLineTotal,
    estimatedTotalDisplay,
    estimatedTotalLabel,
    includedUnitsInOrder,
    handleSubmit,
    handleEditOrder,
    isUpgradeFlow,
    isSubscriptionProduct,
    t,
  };
}

function OrderSummarySubscription({ paymentSectionRef }) {
  const classes = OrderSummarySubscriptionStyles();
  const { t } = useTranslation();
  const state = useOrderSummarySubscriptionState({ paymentSectionRef });

  const estimatedCostLoader = () => (
    <EVGrid container justifyContent="space-between" className={classes.estimatedTotal} wrap="nowrap">
      <EVGrid item xs={6} sm={6} md={6} lg={6}>
        <EVSkeleton style={{ height: '18px', width: '140px', borderRadius: '6px' }} animation="wave" variant="rect" />
      </EVGrid>
      <EVSkeleton style={{ height: '18px', width: '60px', borderRadius: '6px' }} animation="wave" variant="rect" />
    </EVGrid>
  );

  const subscriptionTotals = () => (
    <div className={classes.subscriptionTotals}>
      <EVGrid container justifyContent="space-between" alignItems="center" className={classes.subscriptionTotalRow} wrap="nowrap">
        <Typography className={classes.subscriptionTotalLabel}>
          {t('finalize_order__order_summary__included_in_subscription')}
        </Typography>
        <UnitBadge classes={classes} units={state.includedUnitsInOrder} t={t} />
      </EVGrid>
      <EVGrid container justifyContent="space-between" alignItems="center" className={classes.subscriptionTotalRow} wrap="nowrap">
        <Typography className={classes.subscriptionGrandTotalLabel}>
          {t('finalize_order__order_summary__total_usd')}
        </Typography>
        <Typography className={classes.subscriptionGrandTotalValue}>
          {state.estimatedTotalDisplay ?? '—'}
        </Typography>
      </EVGrid>
    </div>
  );

  const classicEstimatedTotal = (
    <EVGrid container justifyContent="space-between" className={classes.estimatedTotal} wrap="nowrap">
      <EVGrid item xs={6} sm={6} md={6} lg={6}>
        <Typography>{state.estimatedTotalLabel}</Typography>
      </EVGrid>
      <Typography>{state.estimatedTotalDisplay ?? '—'}</Typography>
    </EVGrid>
  );

  const showEstimatedCostLoader = state.fetchingEstimatedCost
    && !(state.isUpgradeFlow && state.isSubscriptionProduct);

  let estimatedCostSection = classicEstimatedTotal;
  if (showEstimatedCostLoader) {
    estimatedCostSection = estimatedCostLoader();
  } else if (state.isSubscriptionProduct) {
    estimatedCostSection = subscriptionTotals();
  }

  return (
    <EVPaper classes={{ root: classes.orderSummary }} square={state.isMobile}>
      {state.placeOrderLoading ? <PlacingOrderLoader /> : null}
      <Typography variant="h3-600" fontColor="ocean-700">{t('finalize_order__order_summary')}</Typography>
      <EVGrid container className={classes.selectedProductsDetailsList}>
        <OrderSummarySubscriptionProductSection
          classes={classes}
          productDisplay={state.productDisplay}
          subscriptionLineItems={state.subscriptionLineItems}
          isEntitledForHidePricing={state.isEntitledForHidePricing}
          overagePriceDisplay={state.overagePriceDisplay}
          showSubscriptionDeliveryLine={state.showSubscriptionDeliveryLine}
          showSubscriptionAddonLine={state.showSubscriptionAddonLine}
          selectedDeliveryMeta={state.selectedDeliveryMeta}
          formatSubscriptionLinePrice={state.formatSubscriptionLinePrice}
          subscriptionDeliveryLineTotal={state.subscriptionDeliveryLineTotal}
          subscriptionAddonLineTotal={state.subscriptionAddonLineTotal}
          t={state.t}
        />
      </EVGrid>
      {estimatedCostSection}
      <EVGrid className={clsx(classes.flexColumn, classes.terms)}>
        <Typography>{t('finalize_order__payment_information__master_billing')}</Typography>
        <Typography>
          {t('finalize_order__order_summary__payment_terms_of_use_1')}
          <EVLink target="_blank" href={LEGACY_PORTAL_LINKS.termsOfUse}>{t('finalize_order__order_summary__payment_terms_of_use_2')}</EVLink>
        </Typography>
      </EVGrid>
      <EVGrid container justifyContent={state.isUpgradeFlow ? 'flex-end' : 'space-between'} className={classes.buttonContainer}>
        {!state.isUpgradeFlow && (
        <EVButton id="edit-order-btn" variant="outlined" color="primary" className={classes.button} onClick={state.handleEditOrder}>
          {t('finalize_order__order_summary__payment_edit_order')}
        </EVButton>
        )}
        <EVButton id="submitOrderBtn" onClick={state.handleSubmit} variant="contained" color="primary" className={classes.button}>
          {t('finalize_order__order_summary__payment_submit_order')}
        </EVButton>
      </EVGrid>
    </EVPaper>
  );
}

OrderSummarySubscription.propTypes = {
  paymentSectionRef: PropTypes.shape({
    current: PropTypes.shape({
      tokenize: PropTypes.func,
    }),
  }),
};

OrderSummarySubscription.defaultProps = {
  paymentSectionRef: null,
};

export default OrderSummarySubscription;
